import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import ProductPicker from "@/components/ProductPicker";
import { TRY } from "@/lib/format";
import { Plus, Trash2 } from "lucide-react";

export const UNITS = ["ADET", "METRE", "KG", "KOLİ", "PAKET", "TAKIM", "SAAT", "HİZMET"];
export const blankInvoiceItem = () => ({ name: "", description: "", code: "", quantity: 1, unit: "ADET", unit_price: 0, discount_rate: 0, product_id: "", brand: "" });
export const netUnitOf = (it) => (Number(it.unit_price) || 0) * (1 - (Number(it.discount_rate) || 0) / 100);
export const lineTotalOf = (it) => (Number(it.quantity) || 0) * netUnitOf(it);

// GİB/QNB ile BİREBİR aynı tutar: her satır brüt→iskonto→net→KDV ayrı ayrı 2 haneye yuvarlanır,
// sonra toplanır (backend invoice_totals / qnb_service.build_invoice_ubl ile aynı mantık).
export const r2 = (x) => Math.round(((Number(x) || 0) + 1e-9) * 100) / 100;
export const ublLineAmounts = (it, kdvRate = 0, globalDiscRate = 0) => {
  const qty = Number(it.quantity) || 0;
  const up = Number(it.unit_price) || 0;
  const gross = r2(qty * up);
  const rate = (Number(it.discount_rate) || 0) || (Number(globalDiscRate) || 0);
  const disc = rate ? r2((gross * rate) / 100) : 0;
  const net = r2(gross - disc);
  const tax = r2((net * (Number(kdvRate) || 0)) / 100);
  return { gross, disc, net, tax };
};
export const ublTotals = (items, kdvRate = 0, globalDiscRate = 0) => {
  let gross = 0, disc = 0, net = 0, tax = 0;
  (items || []).forEach((it) => {
    const a = ublLineAmounts(it, kdvRate, globalDiscRate);
    gross += a.gross; disc += a.disc; net += a.net; tax += a.tax;
  });
  gross = r2(gross); disc = r2(disc); net = r2(net); tax = r2(tax);
  return { sub: gross, disc, net, kdv: tax, total: r2(net + tax) };
};

export const round4 = (x) => Math.round(((Number(x) || 0) + 1e-9) * 10000) / 10000;
// Hedef Genel Toplam: LİSTE FİYATLARI KORUNUR, yalnız satır İSKONTO oranları ayarlanır. 1) netler oransal ölçeklenir
// (%0 iskontoya dayanan satır sabitlenir, kalan fark diğerlerine dağılır) 2) en büyük satırın oranı ikili aramayla hedefe oturur
// 3) gerekirse birkaç satır kuruş kuruş ince ayarlanır. Genel iskonto satırlara katılır (çağıran genel iskontoyu 0 yapar).
// max = iskontosuz (liste fiyatıyla) ulaşılabilecek en yüksek toplam; hedef bunu aşarsa kalemler değişmez.
export const fitDiscountsToTarget = (items, kdvRate = 0, globalDiscRate = 0, target) => {
  const tgt = r2(target);
  const g = Number(globalDiscRate) || 0;
  const src = (items || []).map((it) => ({ ...it, discount_rate: round4((Number(it.discount_rate) || 0) || g) }));
  const total = () => ublTotals(src, kdvRate, 0).total;
  const grossOf = (i) => r2((Number(src[i].quantity) || 0) * (Number(src[i].unit_price) || 0));
  const adj = src.map((_, i) => i).filter((i) => grossOf(i) > 0);
  const max = ublTotals(src.map((it) => ({ ...it, discount_rate: 0 })), kdvRate, 0).total;
  if (!(tgt > 0) || !adj.length || tgt > max) return { items: src, max };
  const setRate = (i, r) => { src[i].discount_rate = Math.min(99.9999, Math.max(0, round4(r))); };
  // 1) oransal net ölçek
  const N = tgt / (1 + (Number(kdvRate) || 0) / 100);
  const base = Object.fromEntries(adj.map((i) => [i, grossOf(i) * (1 - src[i].discount_rate / 100)]));
  let free = [...adj], fixed = 0, s = null;
  for (let k = 0; k <= adj.length; k++) {
    const sumF = free.reduce((a, i) => a + base[i], 0);
    if (!(sumF > 0)) { s = null; break; }
    s = (N - fixed) / sumF;
    const over = free.filter((i) => base[i] * s > grossOf(i));
    if (!over.length) break;
    over.forEach((i) => { fixed += grossOf(i); });
    free = free.filter((i) => !over.includes(i));
  }
  const sumGF = free.reduce((a, i) => a + grossOf(i), 0) || 1;
  adj.forEach((i) => {
    const gr = grossOf(i);
    const net = !free.includes(i) ? gr : (s === null ? gr * (N - fixed) / sumGF : base[i] * s);
    setRate(i, 100 * (1 - net / gr));
  });
  // 2) en büyük satırın oranı (4 hane) ikili aramayla → hedefi aşmayan en yakın
  const L = adj.reduce((a, i) => (grossOf(i) > grossOf(a) ? i : a), adj[0]);
  const at = (k) => { src[L].discount_rate = k / 10000; return total(); };
  let lo = 0, hi = 999999;
  while (lo < hi) { const mid = (lo + hi) >> 1; if (at(mid) <= tgt) hi = mid; else lo = mid + 1; }
  at(lo);
  // satır netini ~dKurus kadar değiştirir (iskonto oranı üzerinden; +kuruş = daha az iskonto)
  const nudge = (i, dKurus) => {
    const gr = grossOf(i);
    if (gr <= 0) return;
    const step = Math.max(0.0001, round4(Math.abs(dKurus) / gr));
    setRate(i, src[i].discount_rate + (dKurus > 0 ? -step : step));
  };
  // 3) kuruş düzeltme: taşmayan en iyi tek hamleyle hedefe yürür; parite tuzağında çift hamleyle çözer
  let guard = 0;
  while (guard++ < 600) {
    const diff = r2(tgt - total());
    if (diff === 0) break;
    const want = diff > 0 ? 1 : -1;
    let chosen = -1, chosenNd = null;
    for (let i = 0; i < src.length; i++) {
      const base = src[i].discount_rate;
      nudge(i, want);
      const nd = r2(tgt - total());
      src[i].discount_rate = base;
      const noOver = want > 0 ? nd >= -1e-9 : nd <= 1e-9;
      if (noOver && (chosenNd === null || Math.abs(nd) < Math.abs(chosenNd))) { chosen = i; chosenNd = nd; }
    }
    if (chosen >= 0) {
      nudge(chosen, want);
      if (chosenNd === 0) break;
      continue;
    }
    // parite tuzağı: (a:+want*sa, b:-want*sb), sa,sb ∈ {1,2,3}
    let done = false;
    for (let a = 0; a < src.length && !done; a++) {
      const ba = src[a].discount_rate;
      for (const sa of [1, 2, 3]) {
        src[a].discount_rate = ba; nudge(a, want * sa);
        for (let b = 0; b < src.length && !done; b++) {
          if (b === a) continue;
          const bb = src[b].discount_rate;
          for (const sb of [1, 2, 3]) {
            src[b].discount_rate = bb; nudge(b, -want * sb);
            if (r2(tgt - total()) === 0) { done = true; break; }
          }
          if (!done) src[b].discount_rate = bb;
        }
        if (done) break;
        src[a].discount_rate = ba;
      }
    }
    break;
  }
  // 4) son çare: 0,0001'lik oran adımlarıyla tek satır / satır çifti taraması (büyük tutarlı az satırda kuruş farkı kalmasın)
  if (r2(tgt - total()) !== 0) {
    const tryStep = (i, k) => { const b = src[i].discount_rate; setRate(i, b - k / 10000); return b; };
    let hit = false;
    for (const i of adj) {
      for (let k = -300; k <= 300 && !hit; k++) { const b = tryStep(i, k); if (r2(tgt - total()) === 0) hit = true; else src[i].discount_rate = b; }
      if (hit) break;
    }
    for (let x = 0; x < adj.length && !hit; x++) {
      for (let y = x + 1; y < adj.length && !hit; y++) {
        for (let ka = -12; ka <= 12 && !hit; ka++) {
          const ba = tryStep(adj[x], ka);
          for (let kb = -12; kb <= 12 && !hit; kb++) { const bb = tryStep(adj[y], kb); if (r2(tgt - total()) === 0) hit = true; else src[adj[y]].discount_rate = bb; }
          if (!hit) src[adj[x]].discount_rate = ba;
        }
      }
    }
  }
  return { items: src, max };
};

// Hedef Genel Toplam: İSKONTO ORANLARI KORUNUR, BİRİM FİYATLAR (4 hane) ayarlanır — iskontosuz faturada yuvarlama bu yolla yapılır.
// 1) fiyatlar oransal ölçeklenir 2) en büyük satırın fiyatı ikili aramayla hedefi aşmayan en yakına 3) tek satır / satır çifti kuruş taraması.
export const fitPricesToTarget = (items, kdvRate = 0, globalDiscRate = 0, target) => {
  const tgt = r2(target);
  const g = Number(globalDiscRate) || 0;
  const src = (items || []).map((it) => ({ ...it, unit_price: Number(it.unit_price) || 0, discount_rate: round4((Number(it.discount_rate) || 0) || g) }));
  const total = () => ublTotals(src, kdvRate, 0).total;
  const adj = src.map((_, i) => i).filter((i) => (Number(src[i].quantity) || 0) > 0 && src[i].unit_price > 0 && src[i].discount_rate < 100);
  const cur = total();
  if (!(tgt > 0) || !adj.length || !(cur > 0)) return { items: src, ok: false };
  adj.forEach((i) => { src[i].unit_price = round4(src[i].unit_price * (tgt / cur)); });
  const val = (i) => (Number(src[i].quantity) || 0) * src[i].unit_price;
  const L = adj.reduce((a, i) => (val(i) > val(a) ? i : a), adj[0]);
  const at = (k) => { src[L].unit_price = k / 10000; return total(); };
  let lo = 1, hi = Math.round(src[L].unit_price * 10000) * 2 + 10000;
  while (lo < hi) { const mid = Math.floor((lo + hi + 1) / 2); if (at(mid) <= tgt) lo = mid; else hi = mid - 1; }
  at(lo);
  const hit = () => r2(tgt - total()) === 0;
  const tryAt = (i, k, base) => { const v = round4(base + k / 10000); if (v <= 0) return false; src[i].unit_price = v; return hit(); };
  if (!hit()) {
    let done = false;
    for (const i of adj) {
      const b = src[i].unit_price;
      for (let k = 1; k <= 400 && !done; k++) done = tryAt(i, k, b) || tryAt(i, -k, b);
      if (done) break;
      src[i].unit_price = b;
    }
    const big = [...adj].sort((a, b) => val(b) - val(a)).slice(0, 10);   // çift taraması en büyük 10 satırla (hız)
    for (let x = 0; x < big.length && !done; x++) {
      for (let y = x + 1; y < big.length && !done; y++) {
        const bx = src[big[x]].unit_price, by = src[big[y]].unit_price;
        for (let ka = -15; ka <= 15 && !done; ka++) {
          tryAt(big[x], ka, bx);
          for (let kb = -15; kb <= 15 && !done; kb++) done = tryAt(big[y], kb, by);
        }
        if (!done) { src[big[x]].unit_price = bx; src[big[y]].unit_price = by; }
      }
    }
  }
  return { items: src, ok: hit() };
};

// Yuvarlama kuralı: kalemlerde iskonto varsa (ve hedef liste toplamını aşmıyorsa) İSKONTO ayarlanır, liste fiyatı korunur;
// iskonto yoksa (ya da hedef liste toplamının üstündeyse) BİRİM FİYAT ayarlanır. Dönen kalemlerde genel iskonto satırlara işlenmiştir (genel = 0).
export const fitToTarget = (items, kdvRate = 0, globalDiscRate = 0, target) => {
  const hasDisc = (Number(globalDiscRate) || 0) > 0 || (items || []).some((it) => (Number(it.discount_rate) || 0) > 0);
  if (hasDisc) {
    const tgt = r2(target);
    const { items: next, max } = fitDiscountsToTarget(items, kdvRate, globalDiscRate, target);
    if (tgt <= max) {
      const dt = ublTotals(next, kdvRate, 0).total;
      if (Math.abs(dt - tgt) < 0.005) return { items: next, method: "discount", total: dt };
      const p = fitPricesToTarget(next, kdvRate, 0, target);   // iskontoyla kapanmayan son kuruş birim fiyatla kapatılır
      const pt = ublTotals(p.items, kdvRate, 0).total;
      return Math.abs(pt - tgt) < Math.abs(dt - tgt) ? { items: p.items, method: "price", total: pt } : { items: next, method: "discount", total: dt };
    }
  }
  const { items: next } = fitPricesToTarget(items, kdvRate, globalDiscRate, target);
  return { items: next, method: "price", total: ublTotals(next, kdvRate, 0).total };
};

const F = ({ label, children, className = "" }) => (
  <div className={`space-y-1 ${className}`}><Label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{label}</Label>{children}</div>
);

// Fatura mal/hizmet kalemleri — teklifteki sistemle aynı: stoktan seç, kod, iskonto %, net birim fiyat
export default function InvoiceItemsEditor({ items, onChange, productToItem, testPrefix = "item" }) {
  const setRow = (i, k, v) => onChange(items.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));
  const rmRow = (i) => onChange(items.filter((_, idx) => idx !== i));
  const addRow = () => onChange([...items, blankInvoiceItem()]);
  const addFromStock = (p) => {
    const next = [...items];
    const idx = next.findIndex((it) => !String(it.name || "").trim() && !it.product_id);
    if (idx >= 0) next[idx] = productToItem(p); else next.push(productToItem(p));
    onChange(next);
  };
  const pickForRow = (i, p) => onChange(items.map((r, idx) => (idx === i ? { ...r, ...productToItem(p) } : r)));

  const Toolbar = ({ pos }) => (
    <div className="flex items-center gap-2" data-testid={`${testPrefix}-toolbar-${pos}`}>
      <ProductPicker onSelect={addFromStock} label="Stoktan Ürün" testid={pos === "top" ? `${testPrefix}-product-picker` : `${testPrefix}-product-picker-bottom`} />
      <Button size="sm" variant="outline" onClick={addRow} data-testid={pos === "top" ? "add-item-row" : "add-item-row-bottom"}><Plus className="h-3.5 w-3.5 mr-1" />Satır Ekle</Button>
    </div>
  );

  return (
    <div className="space-y-2 min-w-0">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Mal / Hizmet Kalemleri · {items.length}</Label>
        <Toolbar pos="top" />
      </div>
      <div className="rounded-lg border divide-y" data-testid={`${testPrefix}-rows`}>
        {items.map((it, i) => {
          const netUnit = netUnitOf(it);
          const line = lineTotalOf(it);
          return (
            <div key={i} className={`p-3 ${i % 2 ? "bg-muted/30" : "bg-background"}`} data-testid={`${testPrefix}-row-${i}`}>
              <div className="flex items-center justify-between gap-2 mb-2">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="h-6 w-6 rounded-full bg-primary text-white text-xs font-bold flex items-center justify-center shrink-0">{i + 1}</span>
                  <ProductPicker label="Stoktan Seç" size="sm" variant={it.product_id ? "secondary" : "outline"} onSelect={(p) => pickForRow(i, p)} testid={`${testPrefix}-pick-${i}`} />
                  {it.product_id ? <span className="text-[10px] text-secondary font-bold uppercase tracking-wide shrink-0">Stok bağlı</span> : null}
                </div>
                <div className="flex items-center gap-4 shrink-0">
                  <div className="text-right">
                    <div className="text-[10px] text-muted-foreground uppercase tracking-wide leading-none mb-0.5">Net B.Fiyat</div>
                    <div className="font-semibold text-sm tabular-nums whitespace-nowrap" data-testid={`${testPrefix}-net-${i}`}>{TRY(netUnit)}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-[10px] text-muted-foreground uppercase tracking-wide leading-none mb-0.5">Tutar</div>
                    <div className="font-head font-black text-base tabular-nums whitespace-nowrap" data-testid={`${testPrefix}-line-${i}`}>{TRY(line)}</div>
                  </div>
                  <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => rmRow(i)} data-testid={`${testPrefix}-del-${i}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                </div>
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-[minmax(0,3fr)_minmax(0,1.5fr)_minmax(96px,1fr)_minmax(110px,1.1fr)_minmax(130px,1.4fr)_minmax(96px,1fr)] gap-2 items-end">
                <F className="col-span-2 lg:col-span-1" label="Ürün / Hizmet Adı"><Input className="h-9 text-sm" placeholder="Malzeme adı" value={it.name} onChange={(e) => setRow(i, "name", e.target.value)} data-testid={`${testPrefix}-name-${i}`} /></F>
                <F label="Kod"><Input className="h-9 text-sm font-mono" placeholder="Kod" value={it.code || ""} onChange={(e) => setRow(i, "code", e.target.value)} data-testid={`${testPrefix}-code-${i}`} /></F>
                <F label="Miktar"><Input className="h-9 text-sm text-right tabular-nums min-w-[90px]" type="number" step="any" inputMode="decimal" value={it.quantity} onChange={(e) => setRow(i, "quantity", e.target.value)} data-testid={`${testPrefix}-qty-${i}`} /></F>
                <F label="Birim">
                  <Select value={(it.unit || "ADET").toUpperCase()} onValueChange={(v) => setRow(i, "unit", v)}>
                    <SelectTrigger className="h-9 text-sm" data-testid={`${testPrefix}-unit-${i}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                  </Select>
                </F>
                <F label="Liste B.Fiyat ₺"><Input className="h-9 text-sm text-right tabular-nums min-w-[110px]" type="number" step="any" inputMode="decimal" value={it.unit_price} onChange={(e) => setRow(i, "unit_price", e.target.value)} data-testid={`${testPrefix}-price-${i}`} /></F>
                <F label="İskonto %"><Input className="h-9 text-sm text-right tabular-nums min-w-[90px]" type="number" step="any" inputMode="decimal" value={it.discount_rate} onChange={(e) => setRow(i, "discount_rate", e.target.value)} data-testid={`${testPrefix}-disc-${i}`} /></F>
                <F className="col-span-2 lg:col-span-6" label="Açıklama (opsiyonel)"><Input className="h-8 text-xs" placeholder="Açıklama" value={it.description || ""} onChange={(e) => setRow(i, "description", e.target.value)} data-testid={`${testPrefix}-desc-${i}`} /></F>
              </div>
            </div>
          );
        })}
      </div>
      <div className="flex items-center justify-end pt-1"><Toolbar pos="bottom" /></div>
    </div>
  );
}
